import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import btnArrow from "../assets/img/section-btn-arrow.webp";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import useCartStore from "../api/cart";
import CartSummary from "./CartSummary";
import { COMMON_BRANDS } from "../utils/brands";
import {
  describeItem,
  itemDescriptionComplete,
} from "../utils/itemDescription";
import { isSweater } from "../utils/sweaterScreen";
import {
  LINED_OPTIONS,
  asksLined,
  linedAnswered,
  pieceFor,
} from "../utils/linedJacket";
import { authHeader } from "../utils/staffAuth";

function generateUniqueId() {
  const randomId = Math.floor(Math.random() * 1e10).toString();
  return randomId.padStart(10, "0");
}

function TypeSelectionScreen() {
  const [isOpen, setIsOpen] = useState(false);
  const toggleBox = () => {
    setIsOpen(!isOpen);
  };
  const [selectedItem, setSelectedItem] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [customObjectName, setCustomObjectName] = useState("");

  const navigate = useNavigate();

  const handleTypeSelectionData = (item) => {
    // Check if the item is already selected
    if (selectedItem && selectedItem?.name === item?.name) {
      // If it's already selected, deselect it
      setSelectedItem(null);
      setSelectedItems([]);
    } else {
      // If it's not selected, select it
      setSelectedItem(item);
      setSelectedItems([item]); // Update selectedItems array with only the new item
      sessionStorage.setItem("selectedItem", item?.name);
      sessionStorage.setItem("selectedItems", JSON.stringify(item));
    }
    // Changing the garment clears the lining answer, so a "Lined" tapped on a
    // jacket can never ride along onto the next piece and price it as a coat.
    setLined("");
  };

  // For anything not on the preset list (a bag, a hat, etc.). Flows through
  // the same Standard/Premium plan step and the same Alteration/Repair
  // step as any real item — it just won't match a preset service, so the
  // custom write-in fields there are what get used. Typing a name below
  // replaces the generic placeholder so the item is properly captured
  // under its own name, not just "Custom Object".
  const handleCustomObjectSelection = () => {
    if (selectedItem && selectedItem?.is_custom) {
      setSelectedItem(null);
      setSelectedItems([]);
      setCustomObjectName("");
      sessionStorage.removeItem("selectedItem");
      sessionStorage.removeItem("selectedItems");
      return;
    }
    const customItem = { name: "", is_custom: true };
    setSelectedItem(customItem);
    setSelectedItems([customItem]);
    setCustomObjectName("");
    sessionStorage.setItem("selectedItem", "");
    sessionStorage.setItem("selectedItems", JSON.stringify(customItem));
  };

  const handleCustomObjectNameChange = (e) => {
    const name = e.target.value;
    setCustomObjectName(name);
    const customItem = { name, is_custom: true };
    setSelectedItem(customItem);
    setSelectedItems([customItem]);
    sessionStorage.setItem("selectedItem", name);
    sessionStorage.setItem("selectedItems", JSON.stringify(customItem));
  };

  const { cartItems, updateCartItem, addToCart } = useCartStore();
  const location = useLocation();
  const [alerMsg, setAlertMsg] = useState(false);

  useEffect(() => {
    setBrand("");
    setNoLabel(false);
    setColor("");
    setFabric("");
    setLined("");
    if (
      cartItems &&
      cartItems?.length > 0 &&
      location?.state?.addNewItem === false
    ) {
      const lastSelectedItem =
        cartItems[0]?.itemsData[cartItems[0]?.itemsData?.length - 1];
      setSelectedItems(cartItems[0]?.itemsData);
      setSelectedItem(lastSelectedItem);
    }
    const item1 = sessionStorage.getItem("selectedItems");
    setSelectedItem(JSON.parse(item1));
    setSelectedItems([JSON.parse(item1)]);
    if (
      !sessionStorage.getItem("addnewitem") &&
      cartItems &&
      cartItems.length > 0 &&
      cartItems[0]?.itemsData?.length >= 1 &&
      cartItems[0]?.services?.length >= 1
    ) {
      // replace, so Back from the basket doesn't bounce straight back here.
      navigate("/item-detail", { replace: true });
    }
  }, []);
  const pincodeData = location.state?.pincode || "";
  const [selectedType, setSelectedType] = useState(() => {
    const savedType = sessionStorage.getItem("selectedType");
    return selectedItems.map((itm) => itm) || "";
  });
  // Brand (or "No label"), Color and Fabric — see utils/itemDescription.js.
  const [brand, setBrand] = useState("");
  const [noLabel, setNoLabel] = useState(false);
  const [color, setColor] = useState("");
  const [fabric, setFabric] = useState("");
  // "Is it lined?", jackets only. A lined jacket is rung up as a coat, the
  // same rule the online store has had since 4 Oct (utils/linedJacket.js).
  const [lined, setLined] = useState("");
  const needsLined = asksLined(selectedItem?.name);
  const itemFields = {
    brand: noLabel ? "" : brand.trim(),
    no_label: noLabel,
    color: color.trim(),
    fabric: fabric.trim(),
  };
  // Also kept as one line: receipts, invoices, exports and HubSpot read it.
  const describe = describeItem(itemFields);
  const described = itemDescriptionComplete(itemFields);

  const handleApply = () => {
    if (!described || !linedAnswered(selectedItem?.name, lined)) {
      setAlertMsg(true);
      return;
    }

    const uniqueid = generateUniqueId();

    // A lined jacket is rung up as a coat (utils/linedJacket.js). Swapped
    // once, here, so the cart, the sessionStorage copy and the screen we
    // navigate to all carry the same piece. `data` is the garment list this
    // screen already fetched, which is where the coat's catalogue item comes
    // from.
    const piece = pieceFor(selectedItem, lined, data);
    const pieces = selectedItems.map((item) => pieceFor(item, lined, data));

    // If no cart exists yet, create a fresh one first
    if (!cartItems || cartItems.length === 0) {
      addToCart({
        zipcode: null,
        zipcodeData: null,
        itemsData: pieces.map((item) => ({
          ...item,
          ...itemFields,
          description: describe,
          id: uniqueid,
        })),
        services: [],
        totalDiscount: 0,
      });

      sessionStorage.setItem(
        "selectedItems",
        JSON.stringify({
          ...piece,
          ...itemFields,
          description: describe,
          id: uniqueid,
        }),
      );

      navigate(isSweater(selectedItem) ? "/sweater" : "/plan", {
        state: {
          selectedItem: { ...piece, ...itemFields, describe },
          id: uniqueid,
        },
      });
      return;
    }

    // If cart already exists, update accordingly
    if (location?.state?.addNewItem) {
      const currentItems = cartItems[0].itemsData || [];
      const combinedItems = [
        ...currentItems,
        ...pieces.map((item) => ({
          ...item,
          ...itemFields,
          description: describe,
          id: uniqueid,
        })),
      ];
      updateCartItem(cartItems[0].id, { itemsData: combinedItems });
    } else {
      updateCartItem(cartItems[0].id, {
        itemsData: [
          ...pieces.map((item) => ({
            ...item,
            ...itemFields,
            description: describe,
            id: uniqueid,
          })),
        ],
      });
    }

    sessionStorage.setItem(
      "selectedItems",
      JSON.stringify({
        ...piece,
        ...itemFields,
        description: describe,
        id: uniqueid,
      }),
    );
    setData([]);
    navigate(isSweater(selectedItem) ? "/sweater" : "/plan", {
      state: {
        selectedItem: { ...piece, ...itemFields, describe },
        id: uniqueid,
      },
    });
  };

  const fetchAllItems = async () => {
    const { data } = await axios.get(
      `${process.env.REACT_APP_API_URL}item/getallitem`,
    );
    // Already most-booked first: the backend sorts it (helper/itemOrder.js),
    // so the popup and the online checkout share one order.
    setData(data);
  };

  useEffect(() => {
    fetchAllItems();
  }, []);

  // Retail products (components/Products.jsx). The button only shows once a
  // product is switched on in superadmin, so the screen is unchanged until
  // there is something to sell. A failed load just leaves it hidden.
  const [hasProducts, setHasProducts] = useState(false);
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}products/for-sale`, {
        headers: authHeader(),
      })
      .then((response) => setHasProducts(response.data?.items?.length > 0))
      .catch(() => setHasProducts(false));
  }, []);
  // Gift cards can be switched off altogether in superadmin, and the button
  // follows that switch. A failed load just leaves it hidden.
  const [giftOn, setGiftOn] = useState(false);
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}gift/options`)
      .then((response) => setGiftOn(Boolean(response.data?.enabled)))
      .catch(() => setGiftOn(false));
  }, []);
  useEffect(() => {
    if (data) {
      setIsLoading(false);
    }
  }, [data]);

  return (
    <section className="items-section same-bg-section relative">
      <div className="container padding-x-all">
        <div className="items-box item-pad-box sec-com-padding flex-box justify-between align-center">
          <CartSummary
            className="mobile-none"
            isOpen={isOpen}
            onToggle={toggleBox}
          />

          <CartSummary
            className="desktop-none"
            isOpen={!isOpen}
            onToggle={toggleBox}
          />

          <div className="item-select-box">
            {/* The sales that are not a garment. A gift card bought WITH a
                repair is a box in the checkout instead, because that is one
                payment for both (components/GiftCard.jsx). */}
            {(hasProducts || giftOn) && (
              <div className="counter-entries">
                {hasProducts && (
                  <button
                    type="button"
                    className="custom-item-btn products-entry"
                    onClick={() => navigate("/products")}
                  >
                    Products
                  </button>
                )}
                {giftOn && (
                  <button
                    type="button"
                    className="custom-item-btn gift-entry"
                    onClick={() => navigate("/gift-card")}
                  >
                    Gift card
                  </button>
                )}
              </div>
            )}
            <h2>
              Which item would you like to <br />
              alter or repair?
            </h2>
            <p className="para">
              Choose one item. You can add another item later on.
            </p>

            <div className="wears-box">
              <ul className="items-wears-listing">
                {!isLoading &&
                  data.map((item, index) => (
                    <>
                      <li key={index}>
                        <button
                          onClick={() => handleTypeSelectionData(item)}
                          className={
                            selectedItems &&
                            selectedItems.some(
                              (selected) => selected?.name === item?.name,
                            )
                              ? "item-selected"
                              : ""
                          }
                        >
                          {item?.name}
                        </button>
                      </li>
                    </>
                  ))}
              </ul>
              <p className="para" style={{ marginTop: "0.75rem" }}>
                Don't see it on the list?
              </p>
              <button
                type="button"
                onClick={handleCustomObjectSelection}
                className={
                  selectedItems &&
                  selectedItems.some((selected) => selected?.is_custom)
                    ? "custom-item-btn item-selected"
                    : "custom-item-btn"
                }
              >
                Custom item
              </button>
              {selectedItem?.is_custom && (
                <div className="field-box" style={{ marginTop: "0.75rem" }}>
                  <label className="label-on-line">What is it?</label>
                  <input
                    type="text"
                    maxLength={60}
                    value={customObjectName}
                    onChange={handleCustomObjectNameChange}
                    placeholder="e.g. tote bag, baseball cap"
                  />
                </div>
              )}
            </div>
            <div className="item-describe-box decription-line-fixed">
              <h5 className="describe-heading adjust-sub-head">
                Describe your item
              </h5>
              <p className="para">
                Tell us about your piece so our fixers can plan the right
                repair.
              </p>
              <div className="item-fields">
                <div className="item-fields-row">
                  <div className="item-field">
                    <label className="item-field-label" htmlFor="item-brand">
                      Brand
                    </label>
                    {/* Autocorrect off on all three boxes: Safari turned
                        "Azazie" into "Amaze" at the counter (28 Oct 2026) and
                        the dress could not be found again. Brand names are
                        proper nouns no dictionary holds, so correcting them
                        can only make them wrong. The list suggests the brands
                        we see often; anything can still be typed. */}
                    <input
                      id="item-brand"
                      className="item-field-input"
                      type="text"
                      value={noLabel ? "" : brand}
                      maxLength={80}
                      disabled={noLabel}
                      onChange={(e) => setBrand(e.target.value)}
                      placeholder="e.g. Babaa, Levi's, Acne Studios"
                      list="item-brand-options"
                      autoCorrect="off"
                      autoCapitalize="words"
                      spellCheck={false}
                    />
                    <datalist id="item-brand-options">
                      {COMMON_BRANDS.map((name) => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                    <label className="item-field-check" htmlFor="item-no-label">
                      <input
                        id="item-no-label"
                        type="checkbox"
                        checked={noLabel}
                        onChange={(e) => setNoLabel(e.target.checked)}
                      />
                      No label / don't know
                    </label>
                  </div>
                  <div className="item-field">
                    <label className="item-field-label" htmlFor="item-color">
                      Color
                    </label>
                    <input
                      id="item-color"
                      className="item-field-input"
                      type="text"
                      value={color}
                      maxLength={80}
                      onChange={(e) => setColor(e.target.value)}
                      placeholder="e.g. cream, navy stripe"
                      autoCorrect="off"
                      spellCheck={false}
                    />
                  </div>
                </div>
                <div className="item-field">
                  <label className="item-field-label" htmlFor="item-fabric">
                    {/* Just the fabric (2026-09-30): "or anything else we
                        should know" drew notes into the fabric field, where
                        they print as part of the garment line. Notes have
                        their own box on the next screen. */}
                    {isSweater(selectedItem) ? "Fiber or stitch" : "Fabric"}{" "}
                    <span className="item-field-optional">(optional)</span>
                  </label>
                  <textarea
                    id="item-fabric"
                    className="item-field-input"
                    value={fabric}
                    maxLength={80}
                    onChange={(e) => setFabric(e.target.value)}
                    autoCorrect="off"
                    spellCheck={false}
                    placeholder="e.g. lace, corduroy, cashmere blend"
                  />
                </div>
              </div>
              {/* Jackets only. A lined jacket is rung up as a coat, which on
                  31 Oct 2026 was $40 more on a hem, a sleeve or a take in and
                  $100 more on a zip. The counter never asked before, so every
                  lined jacket was charged the unlined price. */}
              {needsLined && (
                <div className="item-field">
                  <label className="item-field-label">Is it lined?</label>
                  <div className="lined-choice">
                    {LINED_OPTIONS.map((option) => (
                      <button
                        key={option.key}
                        type="button"
                        className={
                          lined === option.key
                            ? "lined-choice-btn lined-choice-btn-on"
                            : "lined-choice-btn"
                        }
                        aria-pressed={lined === option.key}
                        onClick={() => setLined(option.key)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {alerMsg && (
                <p className="para">
                  {described
                    ? "Please say whether the jacket is lined."
                    : 'Please add the brand (or tick "No label") and the color.'}
                </p>
              )}
            </div>
            <button
              className={`${
                !described ||
                selectedItem == null ||
                !linedAnswered(selectedItem?.name, lined) ||
                (selectedItem?.is_custom && !customObjectName.trim())
                  ? "section-main-btn1"
                  : "section-main-btn"
              }`}
              type="button"
              disabled={
                !described ||
                selectedItem == null ||
                !linedAnswered(selectedItem?.name, lined) ||
                (selectedItem?.is_custom && !customObjectName.trim())
              }
              onClick={handleApply}
            >
              Select a service{" "}
              <span>
                <img src={btnArrow} alt="" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

export default TypeSelectionScreen;
